import {
    DeleteOutlined,
    DownloadOutlined,
    OpenAIOutlined,
    PlusOutlined,
    SendOutlined,
    ThunderboltOutlined,
    UserOutlined
} from "@ant-design/icons";
import {
    Button,
    Card,
    Input,
    Space,
    message as toast,
    Typography,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import { useAppDispatch, useAppSelector } from "../../store";
import {
    addMessage,
    clearConversation,
    HarvenMessage,
    setConversation,
} from "../../store/harvenChatSlice";

const { Title, Text } = Typography;

const SUGGESTIONS = [
  {
    title: "Meilleurs retailers",
    desc: "Quels sont mes meilleurs retailers en France par valeur ?",
    icon: <ThunderboltOutlined style={{ color: "#1890ff" }} />,
  },
  {
    title: "Liste exportable",
    desc: "Export CSV de tous les retailers français actifs",
    icon: <DownloadOutlined style={{ color: "#52c41a" }} />,
  },
  {
    title: "Recherche contact",
    desc: "Donne-moi le contact du Printemps Haussmann",
    icon: <UserOutlined style={{ color: "#722ed1" }} />,
  },
  {
    title: "Procédures SOP",
    desc: "Génère un PDF des règles d'or pour la réception",
    icon: <PlusOutlined style={{ color: "#faad14" }} />,
  },
];

const buildMessage = (
  partial: Omit<HarvenMessage, "id" | "createdAt">
): HarvenMessage => ({
  ...partial,
  id: crypto.randomUUID(),
  createdAt: new Date().toISOString(),
});

const HarvenChat = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.currentUser);
  const messagesByUser = useAppSelector(
    (state) => state.harvenChat.messagesByUser
  );
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const userId = user?.id || "anonymous";
  const conversation = useMemo(
    () => messagesByUser[userId] || [],
    [messagesByUser, userId]
  );

  useEffect(() => {
    if (!messagesByUser[userId]) {
      dispatch(setConversation({ userId, messages: [] }));
    }
  }, [dispatch, messagesByUser, userId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, loading]);

  const history = conversation.map((msg: HarvenMessage) => ({
    role: msg.role,
    content: msg.content,
  }));

  const handleSend = async (textOverride?: string) => {
    const content = (textOverride || input).trim();
    if (!content || loading) return;

    const userMessage = buildMessage({ role: "user", content });
    dispatch(addMessage({ userId, message: userMessage }));
    setInput("");

    try {
      setLoading(true);
      const response = await api.post("/ai/project/harven/chat", {
        message: content,
        history,
      });

      const assistantMessage = buildMessage({
        role: "assistant",
        content: response.answer || "",
        usedSources: response.used_sources,
        downloadUrl: response.download_url,
        downloadFilename: response.download_filename,
      });
      dispatch(addMessage({ userId, message: assistantMessage }));
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur Harven";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    dispatch(clearConversation({ userId }));
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        position: "relative",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      {/* Header compact */}
      <div
        style={{
          padding: "12px 24px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#fff",
          zIndex: 10,
        }}
      >
        <Space size={12}>
          <div style={{ background: "#0F172A", padding: "6px", borderRadius: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <OpenAIOutlined style={{ fontSize: 18, color: "#fff" }} />
          </div>
          <Text strong style={{ fontSize: 16, color: "#111827" }}>
            Harven
          </Text>
        </Space>
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={handleClear}
          size="small"
          style={{ color: "#6b7280" }}
        >
          Nouveau chat
        </Button>
      </div>

      {/* Zone des messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 0",
          scrollBehavior: "smooth",
        }}
      >
        <div style={{ maxWidth: 768, margin: "0 auto", padding: "0 24px" }}>
          {conversation.length === 0 ? (
            <div style={{ padding: "15vh 0" }}>
              <div style={{ textAlign: "center", marginBottom: 48 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 50,
                    background: "#0F172A",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 16,
                  }}
                >
                  <OpenAIOutlined style={{ fontSize: 36 }} />
                </div>
                <Title level={2} style={{ margin: 0, fontWeight: 600, color: "#111827" }}>
                  Comment puis-je vous aider ?
                </Title>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
                  gap: 12,
                }}
              >
                {SUGGESTIONS.map((s, i) => (
                  <Card
                    key={i}
                    hoverable
                    size="small"
                    onClick={() => handleSend(s.desc)}
                    style={{ 
                      borderRadius: 12, 
                      border: "1px solid #e5e7eb",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      background: "#fff"
                    }}
                    bodyStyle={{ padding: "16px" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{s.icon}</span>
                      <Text strong style={{ fontSize: 14, color: "#374151" }}>{s.title}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 13, color: "#6b7280" }}>
                      {s.desc}
                    </Text>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 24 }}>
              {conversation.map((msg: HarvenMessage) => (
                <div
                  key={msg.id}
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "70%",
                      padding: "12px 16px",
                      borderRadius: 14,
                      background: msg.role === "user" ? "#f3f4f6" : "transparent",
                      border: msg.role === "user" ? "1px solid #e5e7eb" : "none",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 16,
                        lineHeight: 1.6,
                        color: "#374151",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        textAlign: msg.role === "user" ? "left" : "left",
                      }}
                    >
                      {msg.content}
                    </div>

                    {msg.downloadUrl && (
                      <div style={{ marginTop: 16 }}>
                        <Button
                          type="default"
                          icon={<DownloadOutlined />}
                          href={msg.downloadUrl}
                          target="_blank"
                          style={{
                            borderRadius: 8,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #e5e7eb",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                        >
                          Télécharger {msg.downloadFilename || "le fichier"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <div
                    style={{
                      maxWidth: "70%",
                      padding: "12px 16px",
                      borderRadius: 14,
                      background: "transparent",
                    }}
                  >
                    <Text type="secondary" italic style={{ fontSize: 15 }}>
                      En train de réfléchir...
                    </Text>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Barre d'entrée flottante */}
      <div
        style={{
          padding: "16px 0 24px 0",
          background: "#ffffff",
          borderTop: conversation.length > 0 ? "1px solid #f0f0f0" : "none",
        }}
      >
        <div style={{ maxWidth: 768, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              position: "relative",
              background: "#fff",
              borderRadius: "16px",
              padding: "10px 14px",
              display: "flex",
              alignItems: "flex-end",
              border: "1px solid #d1d5db",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              transition: "border-color 0.2s",
            }}
          >
            <Input.TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              autoSize={{ minRows: 1, maxRows: 12 }}
              placeholder="Demandez n'importe quoi à Harven..."
              bordered={false}
              style={{
                border: "none",
                borderRadius: 0,
                boxShadow: "none !important",
                outline: "none !important",
                padding: "8px 4px",
                fontSize: 16,
                background: "transparent",
                resize: "none",
                lineHeight: 1.5,
              }}
            />
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              disabled={!input.trim() || loading}
              onClick={() => handleSend()}
              style={{
                marginBottom: 4,
                background: input.trim() ? "#0F172A" : "#e5e5e5",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 34,
                width: 34,
                minWidth: 34,
                transition: "background 0.2s"
              }}
            />
          </div>
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, color: "#9ca3af" }}>
              Harven peut commettre des erreurs. Vérifiez les informations importantes.
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HarvenChat;
